import * as ngCore from '@angular/core'
import { isDeepEqual } from '@attain-apps/data-utils'
import { Range } from '@attain-apps/range'
import { TypeChecker } from '@attain-apps/type-guards'
import { ComponentStore } from '@ngrx/component-store'

export interface State {
  ranges: readonly Range<Date>[]
  editingRanges: readonly Range<Date>[]
  selectedRangeIndex?: number
}

export namespace State {
  export const INITIAL: NoMissing<State> = {
    editingRanges: [],
    ranges: [],
    selectedRangeIndex: undefined,
  }
}

@ngCore.Injectable()
export class ListStore extends ComponentStore<State> {
  /** @see {@link State.ranges} */
  public readonly rangesSg = this.selectSignal(state => state.ranges)

  /** @see {@link State.editingRanges} */
  public readonly editingRangesSg = this.selectSignal(
    state => state.editingRanges,
    // NOTE: Use deep equality check to avoid unnecessary updates: CRUD operations
    // produce new array references even if contents haven't changed, so this
    // prevents effects from triggering unless the actual data changes, avoiding
    // infinite loops and redundant re-computations.
    { equal: isDeepEqual },
  )

  /** @see {@link State.selectedRangeIndex} */
  public readonly selectedRangeIndexSg = this.selectSignal(
    state => state.selectedRangeIndex,
  )

  /** Indicates whether there are ranges being currently edited. */
  public isEditingSg = ngCore.computed(() => this.editingRangesSg().length > 0)

  /**
   * Computed signal for the currently selected item based on
   * {@link State.selectedRangeIndex} and {@link State.ranges}.
   */
  public readonly selectedRangeSg = ngCore.computed(
    (
      selectedRangeIndex = this.selectedRangeIndexSg(),
      ranges = this.editingRangesSg(),
    ) =>
      TypeChecker.isNotNullish(selectedRangeIndex)
        ? ranges[selectedRangeIndex]
        : undefined,
  )

  constructor() {
    super(State.INITIAL)

    // Whenever we start editing, we set first range to be edited.
    ngCore.effect(() => {
      const isEditing = this.isEditingSg()

      if (isEditing) {
        this.patchState({ selectedRangeIndex: 0 })
      }
    })

    // Whenever editing ranges changes, we need to check if we need to select something
    ngCore.effect(() => {
      const editingRanges = this.editingRangesSg()

      ngCore.untracked(() => {
        const selectedIndex = this.selectedRangeIndexSg()

        // If a valid selected index exists and the corresponding range is still present, do nothing
        if (selectedIndex !== undefined && editingRanges[selectedIndex]) {
          return
        } else {
          // Otherwise, ensure the selected index points to a valid range
          // Fallback logic: choose 0 if undefined or out of bounds
          const fallbackIndex = Math.max(
            0,
            Math.min(selectedIndex ?? 0, editingRanges.length - 1),
          )
          this.patchState({ selectedRangeIndex: fallbackIndex })
        }
      })
    })
  }

  /**
   * Starts the editing process by copying the current ranges
   * into a separate editing state and selecting the first range.
   * Editing is only allowed if there are ranges to work with.
   */
  public startEditing() {
    const ranges = this.rangesSg()

    this.patchState({ editingRanges: ranges })
  }

  /**
   * Finalizes the editing process by applying the edited ranges
   * to the main state and clearing the editing state.
   */
  public finishEditing() {
    const editingRanges = this.editingRangesSg()

    this.patchState({
      editingRanges: [],
      ranges: [...editingRanges],
      selectedRangeIndex: undefined,
    })
  }

  /**
   * Cancels the editing process by clearing the editing state
   * without applying any changes.
   */
  public discardEditing() {
    this.patchState({
      editingRanges: [],
      selectedRangeIndex: undefined,
    })
  }

  /**
   *  Adds a new range to the editing list at a specified position, and sets the
   *  newly added range as the active selection.
   *
   *  While this mixes state update and UI selection logic, it improves UX and
   *  simplifies usage.
   *
   * @example
   * - addRange() // adds default range at the beginning
   * - addRange({ place: 'end' }) // adds default range at the end
   * - addRange({ index: 2 }) // adds default range at position 2
   * - addRange({ place: 'end', range: myRange }) // adds custom range at the end
   */
  public addRange(
    options?: {
      range?: Range<Date>
    } & ExactlyOneOf<{ place?: 'start' | 'end'; index?: number }>,
  ) {
    const range =
      options?.range ?? new Range<Date>({ end: undefined, start: undefined })
    const currentRanges = this.editingRangesSg()

    // Check if there's an 'infinite' range that includes everything
    const infiniteRangeIndex = currentRanges.findIndex(
      r => r.includesEverything,
    )

    // If such a range exists, activate it and do not add a new one
    if (infiniteRangeIndex !== -1) {
      this.patchState({ selectedRangeIndex: infiniteRangeIndex })
      return
    }

    // Insert the new range based on specified options
    if (options?.index !== undefined) {
      this._insertRangeAtIndex(range, options.index)
    } else if (options?.place === 'end') {
      this._appendRange(range)
    } else {
      // Default behavior: add at the start
      this._prependRange(range)
    }
  }

  /**
   * Deletes a range from the editing list.
   *
   * @example
   * deleteRange({ current: true }) // deletes currently selected range
   * deleteRange({ index: 2 }) // deletes range at index 2
   */
  public deleteRange(options: ExactlyOneOf<{ index: number; current: true }>) {
    const originalRanges = this.editingRangesSg()

    const indexToDelete = options.current
      ? this.selectedRangeIndexSg()
      : options.index

    if (TypeChecker.isNullish(indexToDelete)) {
      return
    }

    const updatedRanges = originalRanges.filter((_, i) => i !== indexToDelete)

    this.patchState({
      editingRanges: updatedRanges,
    })
  }

  /**
   * Inserts a range into the list at a given index.
   * Updates the list of ranges and selects the newly inserted range.
   */
  private _insertRangeAtIndex(range: Range<Date>, index: number) {
    const currentRanges = this.editingRangesSg()
    const updatedRanges = [
      ...currentRanges.slice(0, index),
      range,
      ...currentRanges.slice(index),
    ]
    this.patchState({
      editingRanges: updatedRanges,
      selectedRangeIndex: index,
    })
  }

  /**
   * Adds a range to the end of the current list of ranges.
   * Automatically updates the selected index to the new range.
   */
  private _appendRange(range: Range<Date>) {
    const currentRanges = this.editingRangesSg()
    const newIndex = currentRanges.length
    this.patchState({
      editingRanges: [...currentRanges, range],
      selectedRangeIndex: newIndex,
    })
  }

  /**
   * Adds a range to the beginning of the current list of ranges.
   * Sets the newly added range as the active selection.
   */
  private _prependRange(range: Range<Date>) {
    const currentRanges = this.editingRangesSg()
    this.patchState({
      editingRanges: [range, ...currentRanges],
      selectedRangeIndex: 0,
    })
  }
}
